import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import NotificationApi from '../api/notifications';
import { Notification } from '../types/notification';

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  refreshNotifications: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  getCriticalNotifications: () => Notification[];
  getUnresolvedActionNotifications: () => Notification[];
  getHighPriorityNotifications: () => Notification[];
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Lightweight polling: only check unread count
  const refreshUnreadCount = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const count = await NotificationApi.getUnreadCount();
      setUnreadCount(count);
    } catch (err: any) {
      if (err.response?.status !== 401) {
        console.error('Error fetching unread count:', err);
      }
    }
  }, []);

  // Full refresh: fetch all notifications (called on demand)
  const refreshNotifications = useCallback(async () => {
    // Only fetch if user is authenticated (token exists)
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const [notifs, count] = await Promise.all([
        NotificationApi.getNotifications(),
        NotificationApi.getUnreadCount()
      ]);

      setNotifications(notifs);
      setUnreadCount(count);
    } catch (err: any) {
      // Silently fail on 401 errors (user not authenticated)
      if (err.response?.status === 401) {
        setNotifications([]);
        setUnreadCount(0);
      } else {
        console.error('Error fetching notifications:', err);
        setError(err.message || 'Failed to fetch notifications');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await NotificationApi.markAsRead(notificationId);

      // Update local state - convert both to strings for comparison
      setNotifications(prev =>
        prev.map(n => String(n._id) === String(notificationId) ? { ...n, read: true, readAt: new Date().toISOString() } : n)
      );

      // Recalculate unread count
      const count = await NotificationApi.getUnreadCount();
      setUnreadCount(count);
    } catch (err: any) {
      console.error('Error marking notification as read:', err);
      throw err;
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await NotificationApi.markAllAsRead();

      // Update local state
      setNotifications(prev =>
        prev.map(n => n.preventDismissal ? n : { ...n, read: true, readAt: new Date().toISOString() })
      );

      // Recalculate unread count
      const count = await NotificationApi.getUnreadCount();
      setUnreadCount(count);
    } catch (err: any) {
      console.error('Error marking all as read:', err);
      throw err;
    }
  }, []);

  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      await NotificationApi.deleteNotification(notificationId);

      // Update local state
      setNotifications(prev => prev.filter(n => n._id !== notificationId));

      // Recalculate unread count
      const count = await NotificationApi.getUnreadCount();
      setUnreadCount(count);
    } catch (err: any) {
      console.error('Error deleting notification:', err);
      throw err;
    }
  }, []);

  const getCriticalNotifications = useCallback(() => {
    return notifications.filter(n => n.priority === 'critical' && !n.read);
  }, [notifications]);

  const getUnresolvedActionNotifications = useCallback(() => {
    return notifications.filter(n => n.requiresAction && !n.actionResolved);
  }, [notifications]);

  const getHighPriorityNotifications = useCallback(() => {
    // Return high/critical priority notifications that are unread or require action
    return notifications.filter(n =>
      (n.priority === 'high' || n.priority === 'critical') &&
      (!n.read || (n.requiresAction && !n.actionResolved))
    );
  }, [notifications]);

  // Initial load
  useEffect(() => {
    refreshNotifications();
  }, [refreshNotifications]);

  // Lightweight polling: only check unread count with staggered intervals
  useEffect(() => {
    // Stagger polling: random delay between 3-5 minutes per user
    const baseInterval = 3 * 60 * 1000; // 3 minutes
    const randomOffset = Math.random() * 2 * 60 * 1000; // 0-2 minutes random
    const pollInterval = baseInterval + randomOffset; // 3-5 minutes total

    const interval = setInterval(() => {
      refreshUnreadCount(); // Only fetch count, not full notifications
    }, pollInterval);

    return () => clearInterval(interval);
  }, [refreshUnreadCount]);

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    loading,
    error,
    refreshNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    getCriticalNotifications,
    getUnresolvedActionNotifications,
    getHighPriorityNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
